import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle2,
  Clock,
  MessageSquare,
  User,
  Building2,
  Facebook,
  Instagram,
  Linkedin,
  AlertCircle,
  Loader
} from 'lucide-react';
import Header from "../Header";
import Footer from "../Footer";
import api from '../api/Api';
import ReCAPTCHA from "react-google-recaptcha";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ContactPage({ isDarkMode, setIsDarkMode }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    document.title = "Contacto";
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoadingServices(true);
    try {
      const response = await api.ListServico();
      
      
      const dados = Array.isArray(response) ? response : [];
      setServices(dados);
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
      // Serviços de fallback
      setServices([
        { id: 1, titulo: 'CCTV' },
        { id: 2, titulo: 'Cabeamento Estruturado' },
        { id: 3, titulo: 'Criação de Websites' },
        { id: 4, titulo: 'Software Personalizado' },
        { id: 5, titulo: 'Software Certificado pela AGT' }
      ]);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const captchaToken = recaptchaRef.current?.getValue();

    if (!captchaToken) {
      setFormStatus('error');
      toast.error("Por favor, confirme o captcha antes de enviar.");
      return;
    }

    setSubmitting(true);
    setFormStatus(null);

    try {
 
      const response = await api.SolicitarContacto({
        ...formData,
        captcha_token: captchaToken
      });

      
      setFormStatus('success');
      recaptchaRef.current?.reset();

      // Reset form após 3 segundos
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
        setFormStatus(null);
      }, 3000);

    } catch (error) {
      console.error('Erro detalhado:', error.response?.data || error.message);
      
      const errorMsg = error.response?.data?.message || 'Erro ao enviar mensagem. Tente novamente.';
      toast.error(errorMsg);
      
      setFormStatus('error');
      recaptchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "geral@angonet.ao",
      link: "mailto:geral@angonet.ao",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Phone,
      title: "Telefone",
      value: "+244 945 574 552",
      link: "tel:+244945574552",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: MapPin,
      title: "Localização",
      value: "Luanda, Município de Viana, Angola",
      link: null,
      color: "from-purple-600 to-violet-600"
    },
    {
      icon: Clock,
      title: "Horário",
      value: "Seg - Sex: 8h - 18h",
      link: null,
      color: "from-blue-600 to-purple-600"
    }
  ];

  const faqs = [
    {
      question: "Qual o prazo médio para desenvolvimento de software?",
      answer: "O prazo varia conforme a complexidade do projeto. Projetos simples: 2-4 semanas. Projetos médios: 1-3 meses. Projetos complexos: 3-6 meses. Fazemos sempre uma análise detalhada antes de definir prazos."
    },
    {
      question: "Oferecem suporte após a entrega do projeto?",
      answer: "Sim! Todos os nossos projetos incluem garantia e suporte técnico. O período varia conforme o tipo de serviço, mas geralmente oferecemos 3-12 meses de suporte incluso."
    },
    {
      question: "Como funciona o processo de orçamento?",
      answer: "Entre em contacto através do formulário, email ou telefone. Agendamos uma reunião para entender suas necessidades e em 2-3 dias úteis enviamos uma proposta detalhada com valores e prazos."
    },
    {
      question: "Trabalham com empresas de que setores?",
      answer: "Atendemos empresas de todos os setores: saúde, educação, comércio, indústria, serviços, governo, entre outros. Nossas soluções são personalizadas para cada segmento."
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
    }`}>
      {/* Animated Background Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 right-0 w-[800px] h-[800px] rounded-full blur-[120px] animate-pulse ${
          isDarkMode ? 'bg-blue-500/10' : 'bg-blue-400/20'
        }`}></div>
        <div className={`absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-[120px] animate-pulse ${
          isDarkMode ? 'bg-purple-500/10' : 'bg-purple-500/20'
        }`} style={{animationDelay: '1s'}}></div>
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-[100px] animate-pulse ${
          isDarkMode ? 'bg-cyan-500/5' : 'bg-cyan-400/15'
        }`} style={{animationDelay: '2s'}}></div>
        
        {/* Floating particles */}
        <div className="absolute top-[20%] left-[15%] w-1 h-1 bg-blue-500 rounded-full animate-float"></div>
        <div className="absolute top-[40%] right-[20%] w-1.5 h-1.5 bg-cyan-500 rounded-full animate-float" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-[60%] left-[30%] w-1 h-1 bg-purple-500 rounded-full animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-[80%] right-[40%] w-2 h-2 bg-blue-400 rounded-full animate-float" style={{animationDelay: '0.5s'}}></div>
        <div className="absolute top-[30%] right-[10%] w-1 h-1 bg-cyan-400 rounded-full animate-float" style={{animationDelay: '1.5s'}}></div>
        <div className="absolute top-[70%] left-[50%] w-1.5 h-1.5 bg-purple-400 rounded-full animate-float" style={{animationDelay: '2.5s'}}></div>
      </div>

      <Header isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />

      {/* Hero Section */}
      <section className={`relative pt-32 pb-20 px-6 overflow-hidden ${
        isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-br from-blue-50 to-purple-50'
      }`}>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-6">
              <MessageSquare size={20} className="text-blue-600" />
              <span className={`text-sm font-semibold ${
                isDarkMode ? 'text-cyan-400' : 'text-blue-600'
              }`}>
                Estamos Aqui Para Ajudar
              </span>
            </div>

            <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Entre em <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Contacto</span>
            </h1>
            <p className={`text-xl mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Tem um projeto em mente? Quer saber mais sobre nossos serviços? 
              Fale connosco e transforme suas ideias em realidade.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="relative -mt-10 px-6 z-10 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div
                  key={index}
                  className={`p-6 rounded-2xl transition-all duration-300 ${
                    isDarkMode
                      ? 'bg-gray-800 border border-white/10 hover:border-cyan-500/50'
                      : 'bg-white border border-gray-200 hover:border-blue-500/50 shadow-xl hover:shadow-2xl'
                  }`}
                >
                  <div className={`inline-flex p-4 bg-gradient-to-r ${info.color} rounded-xl mb-4`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <h3 className={`text-lg font-bold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {info.title}
                  </h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className={`text-sm hover:underline ${
                        isDarkMode ? 'text-cyan-400' : 'text-blue-600'
                      }`}
                    >
                      {info.value}
                    </a>
                  ) : (
                    <p className={`text-sm whitespace-pre-line ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {info.value}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content: Form + Info */}
      <section className="py-20 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className={`rounded-2xl p-8 ${
                isDarkMode ? 'bg-gray-800 border border-white/10' : 'bg-white border border-gray-200 shadow-xl'
              }`}>
                <h2 className={`text-3xl font-bold mb-6 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Envie sua Mensagem
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Nome Completo *
                      </label>
                      <div className="relative">
                        <User size={18} className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Seu nome"
                          disabled={submitting}
                          className={`w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all ${
                            isDarkMode
                              ? 'bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-cyan-500'
                              : 'bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                          } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Email *
                      </label>
                      <div className="relative">
                        <Mail size={18} className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="seu@email.com"
                          disabled={submitting}
                          className={`w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all ${
                            isDarkMode
                              ? 'bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-cyan-500'
                              : 'bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                          } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Phone */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Telefone *
                      </label>
                      <div className="relative">
                        <Phone size={18} className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="+244 XXX XXX XXX"
                          disabled={submitting}
                          className={`w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all ${
                            isDarkMode
                              ? 'bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-cyan-500'
                              : 'bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                          } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Company */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Empresa
                      </label>
                      <div className="relative">
                        <Building2 size={18} className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Sua empresa"
                          disabled={submitting}
                          className={`w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all ${
                            isDarkMode
                              ? 'bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-cyan-500'
                              : 'bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                          } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Subject - Serviços do Backend */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Assunto *
                    </label>
                    <div className="relative">
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        disabled={loadingServices || submitting}
                        className={`w-full px-4 py-3 rounded-lg outline-none transition-all ${
                          isDarkMode
                            ? 'bg-gray-700 border-2 border-gray-600 text-white focus:border-cyan-500'
                            : 'bg-gray-50 border-2 border-gray-200 text-gray-900 focus:border-blue-500'
                        } ${(loadingServices || submitting) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <option value="">
                          {loadingServices ? 'Carregando serviços...' : 'Selecione um assunto'}
                        </option>
                        {!loadingServices && services.map((service) => (
                          <option key={service.id} value={service.titulo}>
                            {service.titulo}
                          </option>
                        ))}
                        {!loadingServices && (
                          <>
                            <option value="Suporte Técnico">Suporte Técnico</option>
                            <option value="Parceria Comercial">Parceria Comercial</option>
                            <option value="Informações Gerais">Informações Gerais</option>
                            <option value="Outros">Outros</option>
                          </>
                        )}
                      </select>
                      {loadingServices && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <Loader size={18} className="animate-spin text-blue-600" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Mensagem *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      placeholder="Descreva seu projeto ou dúvida..."
                      disabled={submitting}
                      className={`w-full px-4 py-3 rounded-lg outline-none transition-all resize-none ${
                        isDarkMode
                          ? 'bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-cyan-500'
                          : 'bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                      } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    ></textarea>
                  </div>

                  {/* ReCAPTCHA */}
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      ref={recaptchaRef}
                      theme={isDarkMode ? 'dark' : 'light'}
                    />
                  </div>

                  {/* Success/Error Message */}
                  {formStatus === 'success' && (
                    <div className="flex items-start space-x-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg animate-slide-in">
                      <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={24} />
                      <p className="text-green-500 font-medium">
                        Mensagem enviada com sucesso! Entraremos em contacto em breve.
                      </p>
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="flex items-start space-x-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg animate-slide-in">
                      <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={24} />
                      <p className="text-red-500 font-medium">
                        Erro ao enviar mensagem. Por favor, tente novamente.
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting || loadingServices}
                    className={`w-full flex items-center justify-center space-x-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-2xl hover:shadow-blue-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      submitting ? 'animate-pulse' : ''
                    }`}
                  >
                    {submitting ? (
                      <>
                        <Loader size={20} className="animate-spin" />
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>Enviar Mensagem</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar Info */}
            <div className="space-y-6">
              {/* Quick Contact */}
              <div className={`rounded-2xl p-6 ${
                isDarkMode ? 'bg-gray-800 border border-white/10' : 'bg-white border border-gray-200 shadow-lg'
              }`}>
                <h3 className={`text-xl font-bold mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Contacto Rápido
                </h3>
                <div className="space-y-4">
                  <a
                    href="mailto:geral@angonet.ao"
                    className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Mail className={isDarkMode ? 'text-cyan-400' : 'text-blue-600'} size={20} />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      geral@angonet.ao
                    </span>
                  </a>
                  <a
                    href="tel:+244945574552"
                    className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Phone className={isDarkMode ? 'text-cyan-400' : 'text-blue-600'} size={20} />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      +244 945 574 552
                    </span>
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className={`rounded-2xl p-6 ${
                isDarkMode ? 'bg-gray-800 border border-white/10' : 'bg-white border border-gray-200 shadow-lg'
              }`}>
                <h3 className={`text-xl font-bold mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Redes Sociais
                </h3>
                <div className="flex space-x-3">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.facebook.com/p/AngoNet-Com%C3%A9rcio-e-Presta%C3%A7%C3%A3o-de-Servi%C3%A7o-100094532778927/"
                    className={`p-3 rounded-lg transition-all ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-blue-600 text-white'
                        : 'bg-gray-100 hover:bg-blue-600 text-gray-700 hover:text-white'
                    }`}
                  >
                    <Facebook size={20} />
                  </a>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.instagram.com/_angonet/" 
                    className={`p-3 rounded-lg transition-all ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 text-white'
                        : 'bg-gray-100 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 text-gray-700 hover:text-white'
                    }`}
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.linkedin.com/company/angonet/?viewAsMember=true" 
                    className={`p-3 rounded-lg transition-all ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-blue-600 text-white'
                        : 'bg-gray-100 hover:bg-blue-600 text-gray-700 hover:text-white'
                    }`}
                  >
                    <Linkedin size={20} />
                  </a>
                </div>
              </div>

              {/* Response Time */}
              <div className={`rounded-2xl p-6 bg-gradient-to-br ${
                isDarkMode ? 'from-blue-500/10 to-purple-500/10 border border-white/10' : 'from-blue-50 to-purple-50 border border-blue-200'
              }`}>
                <div className="flex items-start space-x-3">
                  <Clock className={isDarkMode ? 'text-cyan-400' : 'text-blue-600'} size={24} />
                  <div>
                    <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Tempo de Resposta
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Respondemos a todas as mensagens em até 24 horas úteis.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs 
      <section className={`py-20 px-6 ${
        isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-br from-blue-50 to-purple-50'
      }`}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl md:text-5xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Perguntas <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Frequentes</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className={`group rounded-xl overflow-hidden ${
                  isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-gray-200 shadow-lg'
                }`}
              >
                <summary className={`cursor-pointer p-6 font-semibold list-none flex items-center justify-between ${
                  isDarkMode ? 'text-white hover:text-cyan-400' : 'text-gray-900 hover:text-blue-600'
                } transition-colors`}>
                  <span>{faq.question}</span>
                  <span className="text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className={`px-6 pb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>*/}

      <Footer isDarkMode={isDarkMode} />

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
              <ToastContainer 
          position="top-right" 
          autoClose={3000}
          toastStyle={{ borderRadius: 12 }}
        />
    </div>
  );
}